// Keep this small dependency-free client in sync with printbox_lending/src/lib/analyticsClient.js.
export function createAnalytics({ app, endpoint, credentials = () => ({}), language = () => undefined }) {
    const uuid = () => window.crypto?.randomUUID?.() ||
        "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = Math.floor(Math.random() * 16);
            return (c === "x" ? r : (r & 3) | 8).toString(16);
        });
    const storage = (key, initial) => {
        try {
            const saved = sessionStorage.getItem(key);
            if (/^[0-9a-f-]{36}$/i.test(saved || "")) return saved;
            sessionStorage.setItem(key, initial);
        } catch { /* Storage restrictions must not break the app. */ }
        return initial;
    };
    let sessionId = storage(`pb.analytics.${app}.session`, uuid());
    const visitorId = app === "landing" ? storage("pb.analytics.landing.visitor", uuid()) : undefined;
    let active = false;
    let queue = [];
    let running = false;
    let initialized = false;
    let lastRoute = null;
    let lastLanguage = null;
    let sequence = 0;
    let currentRoute = normalizeRoute(window.location.pathname);

    function normalizeRoute(path) {
        // Never send query strings, document IDs or user IDs from dynamic URLs.
        const clean = path.split(/[?#]/)[0].replace(/\/[0-9]+(?=\/|$)/g, "/:id");
        return /^\/[a-zA-Z0-9_:/.-]{0,149}$/.test(clean) ? clean : "/unknown";
    }

    function enqueue(name, properties = {}) {
        try {
            const lang = language();
            const safe = { ...properties };
            if (["ru", "kk", "kz", "en"].includes(lang)) safe.language = lang;
            // Credentials exist only in memory, never in event JSON or persisted queues.
            queue.push({ headers: { ...credentials() }, queuedAt: Date.now(), sequence: ++sequence,
                event: { event_id: uuid(), session_id: sessionId, visitor_id: visitorId,
                    occurred_at: new Date().toISOString(), name, route: currentRoute, properties: safe } });
            if (queue.length > 200) queue = queue.slice(-200);
            if (queue.length >= 15) void flush();
        } catch { /* Analytics is best-effort and must never interrupt printing. */ }
    }

    function track(name, properties = {}) {
        if (app === "kiosk" && !active && name === "ui_error") { enqueue(name, properties); return; }
        if (!active) {
            active = true;
            enqueue("session_start");
        }
        enqueue(name, properties);
    }

    async function flush() {
        if (running || !endpoint || navigator.onLine === false) return;
        queue = queue.filter((item) => Date.now() - item.queuedAt < 15 * 60 * 1000);
        if (!queue.length) return;
        running = true;
        const first = queue[0];
        const snapshot = JSON.stringify(first.headers);
        const batch = queue.filter((item) => JSON.stringify(item.headers) === snapshot).slice(0, 25);
        const headers = { "Content-Type": "application/json", ...first.headers };
        // Device identity can arrive asynchronously from Electron, even before login.
        if (app === "kiosk" && !headers["Device-Token"]) {
            headers["Device-Token"] = credentials()["Device-Token"];
            if (!headers["Device-Token"]) { running = false; return; }
        }
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        let consumed = false;
        try {
            const response = await fetch(endpoint, { method: "POST", headers,
                body: JSON.stringify({ app, events: batch.map((item) => item.event) }),
                keepalive: true, credentials: "omit", signal: controller.signal });
            consumed = response.ok || (response.status >= 400 && response.status < 500 && response.status !== 429);
            if (consumed) {
                const sent = new Set(batch.map((item) => item.sequence));
                queue = queue.filter((item) => !sent.has(item.sequence));
            }
        } catch { /* Retry on the next tick, retaining the original event IDs. */ }
        finally { clearTimeout(timeout); running = false; }
        if (consumed && queue.length) void flush();
    }

    function endSession(reason = "reset") {
        if (active) enqueue("session_end", { reason });
        active = false;
        lastRoute = null;
        sessionId = uuid();
        try { sessionStorage.setItem(`pb.analytics.${app}.session`, sessionId); } catch { /* optional */ }
        void flush();
    }

    function pageView(path) {
        currentRoute = normalizeRoute(path);
        if (currentRoute === lastRoute) return;
        lastRoute = currentRoute;
        // A kiosk's idle home screen is not a customer session. First interaction starts it.
        if (app === "kiosk" && currentRoute === "/" && !active) return;
        track("page_view");
    }

    function init() {
        if (initialized) return;
        initialized = true;
        document.addEventListener("click", (event) => {
            const element = event.target instanceof Element ? event.target.closest("[data-analytics]") : null;
            const target = element?.getAttribute("data-analytics");
            if (target && /^[a-zA-Z0-9_.:-]{1,100}$/.test(target) && !element.disabled) {
                currentRoute = normalizeRoute(window.location.pathname);
                track("button_click", { target });
            }
        }, true);
        window.addEventListener("pagehide", () => void flush());
        window.addEventListener("online", () => void flush());
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") void flush();
        });
        window.addEventListener("error", (event) => {
            if (event.error) track("ui_error", { status: "runtime", source: "ui" });
        });
        window.addEventListener("unhandledrejection", () => track("ui_error", { status: "promise", source: "ui" }));
        setInterval(() => void flush(), 5000);
    }

    function languageChanged(value) {
        if (value === lastLanguage) return;
        const wasSet = lastLanguage !== null;
        lastLanguage = value;
        if (wasSet && (app !== "kiosk" || active)) track("language_changed");
    }
    return { init, track, flush, endSession, pageView, languageChanged };
}
